import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'

import { Store } from '@ngrx/store'
import { OAuthService } from 'angular-oauth2-oidc'
import { SplitAreaComponent, SplitComponent } from 'angular-split'
import { get, isNil, set } from 'lodash'
import { isEmpty } from 'lodash/fp'
import { TooltipModule } from 'primeng/tooltip'

import { catchError, debounceTime, filter, of, switchMap, tap } from 'rxjs'

import { TemplateRendererComponent } from '@flowx/angular-sdk'
import { FlxButtonComponent } from '@flowx/angular-ui-toolkit'
import { FlxCardModule } from '@flowx/angular-ui-toolkit'
import { ProcessData, ProcessDataStore } from '@flowx/core-sdk'
import { getTenantId } from '@flowx/designer/modules/auth/tenant-id.state'
import { FlxClickStopPropagationDirective } from '@flowx/designer/modules/core/directives/stop-propagation.directive'
import {
  CodeEditorFlxLanguage,
  MonacoEditorConstructionOptions,
} from '@flowx/designer/modules/monaco-code-editor/types'
import { PanelSize } from '@flowx/designer/modules/process-details/process-details-helper.service'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { CodeEditorComponent } from '@flowx/designer/modules/script-builder/components/code-editor/code-editor.component'
import { FlxTableDateFormatPipe } from '@flowx/designer/pipes/flx-date.pipe'
import { ExpandablePanelComponent } from '@flowx/designer/shared/components/expandable-panel/expandable-panel.component'
import { ApplicationType } from '@flowx/designer/shared/entities/application.entity'
import {
  getSelectedNodesAndSlice,
  unflattenObject,
} from '@flowx/designer/shared/entities/data-model.entity'
import { ReusableTemplatesService } from '@flowx/designer/shared/services/reusable-template.service'
import {
  ReusableTemplateApiParameters,
  UrlParametersStoreService,
} from '@flowx/designer/shared/services/url-parameters-store.service'
import { ReusableUiTemplateDataModelActions } from '@flowx/designer/shared/store/actions/reusable-ui-template-data-model.actions'
import { selectDataModelTree } from '@flowx/designer/shared/store/selectors/data-model.selectors'

import { FlxReusableTemplateInfoComponent } from '../../components/reusable-template-info/reusable-template-info.component'
import { ReusableUiTemplateStore } from '../../reusable-ui-template.store'

@Component({
  selector: 'flx-reusable-template-instance',
  imports: [
    TemplateRendererComponent,
    RouterLink,
    FlxClickStopPropagationDirective,
    FlxButtonComponent,
    FlxCardModule,
    TooltipModule,
    FlxReusableTemplateInfoComponent,
    ExpandablePanelComponent,
    CodeEditorComponent,
    SplitComponent,
    SplitAreaComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  template: `
    @if (this.templateConfig() && this.reusableInputData() && this.dataModel()) {
      <div
        [style.z-index]="1007"
        class="reusable-test-options-panel flex flex-col absolute bg-white top-5 rounded-lg p-2 left-4 transition-[transform] duration-200 ease-in-out will-change-[transform]"
      >
        <a [routerLink]="['/']">
          <button
            pTooltip="Home"
            tooltipStyleClass="designerTooltip"
            flxButton
            variant="text"
            icon="center"
          >
            <img
              class="w-4 h-4"
              flxIcon
              alt="flowx logo"
              src="./assets/logo-small.svg"
            />
          </button>
        </a>
        <button
          (click)="navigateToConfig()"
          pTooltip="UI Designer"
          tooltipStyleClass="designerTooltip"
          clickStopPropagation
          flxButton
          variant="text"
          icon="center"
        >
          <i
            class="ph ph-paint-brush w-4 h-4 text-gray-800"
            flxIcon
          ></i>
        </button>
        <button
          (click)="inputOutputConsole.togglePanel()"
          pTooltip="Test Reusable UI Template Input / Output Parameters"
          tooltipStyleClass="designerTooltip"
          clickStopPropagation
          flxButton
          variant="text"
          icon="center"
        >
          <i
            class="ph ph-arrows-left-right w-4 h-4 text-gray-800"
            flxIcon
          ></i>
        </button>
        <button
          (click)="navigateToList()"
          pTooltip="Reusable Resources: UI Templates"
          tooltipStyleClass="designerTooltip"
          clickStopPropagation
          flxButton
          variant="text"
          icon="center"
        >
          <i
            class="ph ph-diamonds-four w-4 h-4 text-gray-800"
            flxIcon
          ></i>
        </button>
        <button
          (click)="showReusableTemplateInfo.set(!showReusableTemplateInfo())"
          clickStopPropagation
          flxButton
          variant="text"
          icon="center"
        >
          <i
            class="ph ph-info w-4 h-4 text-gray-800"
            flxIcon
          ></i>
        </button>
        @if (showReusableTemplateInfo()) {
          <ng-container>
            <flx-card
              [style.z-index]="1007"
              [style.min-width.px]="400"
              [style.box-shadow]="'0px 4px 24px rgba(0, 0, 0, 0.24)'"
              class="flex flex-col absolute bg-white top-0 left-16 rounded-lg p-2"
              cardStyle="raised"
            >
              <flx-reusable-template-info
                [reusableInfo]="reusableInfo()"
                (goToReusableTemplateClicked)="navigateToConfig()"
              >
                Config Reusable UI Template - UI Designer
              </flx-reusable-template-info>
            </flx-card>
          </ng-container>
        }
      </div>

      <div class="reusable-test-template-container overflow-auto">
        <flx-template-renderer
          #templateRenderer
          [apiUrl]="apiUrl"
          [authToken]="authToken()"
          [processApiPath]="processApiPath"
          [staticAssetsPath]="staticAssetsPath"
          [projectId]="urlParams.appId"
          [projectVersionId]="urlParams.appVersionId"
          [workspaceId]="urlParams.workspaceId"
          [organizationId]="organizationId"
          [dataModel]="dataModel()"
          [themeId]="themeId()"
          [locale]="locale()"
          [language]="language()"
          [reusableStartData]="reusableInputData()"
          [templateConfig]="templateConfig()"
          (dataStoreChanged)="dataStoreChanged($event)"
        />
      </div>

      <flx-bottom-panel
        #inputOutputConsole
        class="left-0"
      >
        <ng-container flxExpandPanelHeader>
          <button
            (click)="inputOutputConsole.togglePanel()"
            class="flex items-center gap-2 justify-start bg-transparent border-none shadow-none p-0 m-0 rounded-none outline-none focus:outline-none w-full"
          >
            <i
              class="ph ph-arrows-left-right w-4 h-4 text-gray-800"
              flxIcon
            ></i>
            <span class="font-semibold text-xs"
              >Test Reusable UI Template Input / Output Parameters</span
            >
          </button>
        </ng-container>
        <ng-container class="panel-main-container">
          <div class="flex flex-col gap-4 pr-20 pb-5 pl-24 pt-4 h-full">
            <as-split
              [useTransition]="true"
              class="w-full "
              direction="horizontal"
            >
              <as-split-area
                [size]="50"
                [minSize]="10"
                class="flex flex-col gap-3 pr-1"
              >
                <div class="h-10">
                  <p class="text-neutral-900 text-sm font-semibold">Input Parameters</p>
                  <p class="text-xs text-neutral-500">
                    Variables expected by the Reusable UI template
                  </p>
                </div>
                <flx-code-editor
                  #inputCodeEditor
                  [options]="editorOptions"
                  [formControl]="inputFC"
                  [forceShowErrors]="true"
                  class="grow h-1"
                />
              </as-split-area>
              <as-split-area
                [size]="50"
                [minSize]="10"
                class="flex flex-col gap-3 pl-1"
              >
                <div class="h-10">
                  <p class="text-neutral-900 text-sm font-semibold">Output Parameters</p>
                  <p class="text-xs text-neutral-500">Preview Reusable UI Template output</p>
                </div>
                <flx-code-editor
                  [options]="editorOptions"
                  [readonly]="true"
                  [ngModel]="outputModel()"
                  class="grow h-1"
                />
              </as-split-area>
            </as-split>
            <button
              (click)="inputOutputConsole.togglePanel()"
              class="flx-ds primary self-end"
              flxButton
            >
              Close
            </button>
          </div>
        </ng-container>
      </flx-bottom-panel>
    }
  `,
  styles: [
    `
      .as-split-gutter {
        background-color: white !important;
      }

      .reusable-test-template-container {
        @apply transition-[height] duration-200 ease-in-out will-change-[height] h-[calc(100vh-10rem)];
      }

      flx-reusable-template-instance:has(flx-bottom-panel.medium)
        .reusable-test-template-container {
        @apply h-[calc(100vh-60%-200px)];
      }

      flx-reusable-template-instance:has(flx-bottom-panel.large) .reusable-test-options-panel {
        @apply -translate-x-12;
      }
    `,
  ],
  host: { class: `block py-20 pr-20 pl-24 h-full overflow-auto flex flex-col h-full` },
  providers: [ReusableTemplatesService, ReusableUiTemplateStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FlxReusableTemplateInstanceComponent implements OnInit {
  #route = inject(ActivatedRoute)
  #router = inject(Router)
  #reusableTemplatesService = inject(ReusableTemplatesService)
  #store = inject(ReusableUiTemplateStore)
  #ngrxStore = inject(Store)
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #destroyRef = inject(DestroyRef)
  #flxDateFormatPipe = inject(FlxTableDateFormatPipe)
  #authService = inject(OAuthService)
  #panelHydrated = false

  urlParams: ReusableTemplateApiParameters = inject(UrlParametersStoreService).urlParameters
  apiUrl = inject('BASE_URL' as any)
  staticAssetsPath = inject('STATIC_ASSETS_PATH' as any)
  processApiPath = inject('PROCESS_API_PATH' as any)
  authToken = signal(localStorage.getItem('access_token'))
  organizationId = getTenantId()

  templateRenderer = viewChild<TemplateRendererComponent>('templateRenderer')
  inputCodeEditor = viewChild<CodeEditorComponent>('inputCodeEditor')
  inputOutputConsole = viewChild<ExpandablePanelComponent>('inputOutputConsole')

  reusableInputData = signal<ProcessData>(null)
  reusableOutputData = signal<ProcessData>({})
  themeId = signal<string>(null)
  language = signal<string>(null)
  locale = signal<string>(null)
  showReusableTemplateInfo = signal<boolean>(false)
  dataModel = toSignal(
    this.#reusableTemplatesService
      .getReusableTemplateDataModel(this.urlParams.reusableTemplateResourceId)
      .pipe(catchError(() => of({})))
  )

  reusableInfo = computed(() => ({
    appName: this.#applicationDetailsStore.appName(),
    appVersionId: this.#applicationDetailsStore.appVersionId(),
    branchName: this.#applicationDetailsStore.branchName(),
    status: this.#applicationDetailsStore.isVersionCommitted() ? 'COMMITTED' : 'WIP',
    type: this.#applicationDetailsStore.appType() === ApplicationType.APP ? 'Project' : 'Library',
  }))

  outputModel = linkedSignal(() => JSON.stringify(this.reusableOutputData(), null, 2))

  inputFC = new FormControl('{}')

  templateConfig = toSignal(
    this.#route.parent.queryParams.pipe(
      tap((params) => {
        this.themeId.set(params.themeId)
        this.language.set(params.language)
        this.locale.set(params.locale)

        const inputData = window.history.state?.reusableStartData
        if (inputData) {
          this.reusableInputData.set(inputData)
          this.inputFC.setValue(JSON.stringify(inputData, null, 2))
        }
      }),
      switchMap(() =>
        this.#reusableTemplatesService.getReusableTemplateConfig(
          this.urlParams.reusableTemplateResourceId
        )
      ),
      catchError(() => of(null))
    )
  )

  readonly editorOptions: MonacoEditorConstructionOptions = {
    language: CodeEditorFlxLanguage.Json,
    lineNumbers: 'on',
    glyphMargin: false,
    lineDecorationsWidth: 0,
    lineNumbersMinChars: 0,
  }

  outputSlice = computed(() => this.#store.outputSlice().sliceItems)
  inputSlice = computed(() => this.#store.inputSlice().sliceItems)
  reusableTemplateDMTree = this.#ngrxStore.selectSignal(selectDataModelTree)

  constructor() {
    effect(() => {
      const panel = this.inputOutputConsole()
      if (!panel) {
        return
      }
      const size = panel.size()

      if (!this.#panelHydrated) {
        this.#panelHydrated = true
        const openFromUrl = this.#route.snapshot.queryParamMap.get('instanceDataOpened') === 'true'
        if (openFromUrl && size === PanelSize.SMALL) {
          untracked(() => panel.togglePanel())
        }
        return
      }
      const opened = size !== PanelSize.SMALL
      const currentOpened = this.#route.snapshot.queryParamMap.get('instanceDataOpened') === 'true'
      if (currentOpened === opened) {
        return
      }
      untracked(() => {
        this.#router.navigate([], {
          relativeTo: this.#route,
          queryParams: { instanceDataOpened: opened ? true : null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        })
      })
    })

    effect(() => {
      const data = untracked(() => this.reusableInputData())
      const newOutputData = this.#updateOutputData(data)
      untracked(() => this.outputModel.set(JSON.stringify(newOutputData, null, 2)))
    })

    effect(() => {
      const inputSlice = this.inputSlice()
      const reusableTemplateDMTree = this.reusableTemplateDMTree()
      const reusableInputData = untracked(() => this.reusableInputData())
      if (inputSlice && reusableTemplateDMTree && isEmpty(reusableInputData)) {
        untracked(() => {
          this.reusableInputData.set(
            unflattenObject(
              getSelectedNodesAndSlice(
                reusableTemplateDMTree.tree || [],
                inputSlice,
                [],
                false,
                true,
                (date: string) => this.#flxDateFormatPipe.transform(date, false),
                true
              ).exampleValues
            ) as ProcessData
          )
          this.inputFC.setValue(JSON.stringify(this.reusableInputData(), null, 2))
        })
      }
    })
  }

  ngOnInit(): void {
    this.#authService.events
      .pipe(
        filter((event) => event.type === 'token_received'),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.authToken.set(this.#authService.getAccessToken())
      })

    const { appId, appVersionId, reusableTemplateResourceId } = this.urlParams
    this.#applicationDetailsStore.getApplicationVersion(appId, appVersionId)
    this.#store.getReusableUiTemplate(reusableTemplateResourceId)
    this.#store.getSlices(reusableTemplateResourceId)

    this.#ngrxStore.dispatch(
      ReusableUiTemplateDataModelActions.loadEntities({
        resourceId: reusableTemplateResourceId,
      })
    )

    this.inputFC.valueChanges
      .pipe(debounceTime(1000), takeUntilDestroyed(this.#destroyRef))
      .subscribe((value) => {
        if (this.inputCodeEditor()?.errorMarkers().length) {
          return
        }
        try {
          this.templateRenderer().updateReusableDataStore(JSON.parse(value))
        } catch (error) {
          console.error('Error updating input model', error)
        }
      })
  }

  navigateToConfig(): void {
    this.#router.navigate([
      '/guard',
      'workspace',
      this.urlParams.workspaceId,
      'projects',
      this.urlParams.appId,
      'config',
      this.urlParams.appVersionId,
      'reusable-resources',
      'ui-templates',
      this.urlParams.reusableTemplateResourceId,
      'ui',
    ])
  }

  navigateToList(): void {
    this.#router.navigate([
      '/guard',
      'workspace',
      this.urlParams.workspaceId,
      'projects',
      this.urlParams.appId,
      'config',
      this.urlParams.appVersionId,
      'reusable-resources',
      'ui-templates',
    ])
  }

  dataStoreChanged(data: ProcessDataStore): void {
    this.reusableOutputData.set(this.#updateOutputData(data))
  }

  #updateOutputData(data: ProcessData): ProcessData {
    const outputSlice = this.outputSlice()
    const newOutputData = {}

    outputSlice?.forEach((slice) => {
      const value = get(data, slice.stringPath)
      if (!isNil(value) || !slice.optional) {
        set(newOutputData, slice.stringPath, value)
      }
    })

    return newOutputData
  }
}
